'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: `/${lang}#services`, label: 'Services' },
    { href: `/${lang}#how-we-help`, label: t('Approche', 'Approach') },
    { href: `/${lang}/blog`, label: 'Blog' },
    { href: `/${lang}#contact`, label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-dark-900/70 backdrop-blur-xl shadow-[0_1px_0_rgba(255,255,255,0.02),0_4px_16px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
      }`}
      role="navigation"
      aria-label={t('Navigation principale', 'Main navigation')}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href={`/${lang}`} className="flex items-center gap-2.5">
            <Image
              src="/quorova_logo_mark_white_blue.png"
              alt=""
              width={32}
              height={32}
              className="rounded-lg"
              priority
            />
            <span className="font-display text-xl font-bold tracking-[0.15em] uppercase text-white">
              Quorova
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-7">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-white/60 hover:text-white transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop right */}
          <div className="hidden md:flex items-center gap-4">
            <div
              className="flex items-center rounded-lg p-0.5 bg-white/[0.06]"
              role="group"
              aria-label={t('Sélection de langue', 'Language selection')}
            >
              <button
                onClick={() => setLang('fr')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  lang === 'fr'
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white/70'
                }`}
                aria-pressed={lang === 'fr'}
              >
                FR
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  lang === 'en'
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white/70'
                }`}
                aria-pressed={lang === 'en'}
              >
                EN
              </button>
            </div>

            <a
              href={`/${lang}#contact`}
              className="inline-flex items-center px-5 py-2 text-sm font-semibold text-dark-950 bg-warm rounded-lg hover:bg-warm-bright transition-all duration-300 shadow-[0_0_20px_rgba(232,164,74,0.2)] hover:shadow-[0_0_30px_rgba(232,164,74,0.35)]"
            >
              {t('Demander un audit', 'Request an audit')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-white/60 hover:text-white transition-colors"
            aria-expanded={open}
            aria-label="Menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              {open ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu — full screen overlay */}
        {open && (
          <div className="md:hidden absolute left-0 right-0 top-16 px-6 pt-8 pb-6 bg-dark-950/98 backdrop-blur-2xl space-y-2">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="block text-2xl font-display font-semibold py-3 text-white/70 hover:text-white border-b border-white/[0.06] transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}

            <div className="flex items-center gap-4 pt-5">
              <div className="flex items-center gap-1 rounded-lg p-0.5 bg-white/[0.06]">
                <button
                  onClick={() => setLang('fr')}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                    lang === 'fr' ? 'bg-white/10 text-white' : 'text-white/60'
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang('en')}
                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                    lang === 'en' ? 'bg-white/10 text-white' : 'text-white/60'
                  }`}
                >
                  EN
                </button>
              </div>
              <a
                href={`/${lang}#contact`}
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-6 py-3 text-sm font-semibold text-dark-950 bg-warm rounded-xl hover:bg-warm-bright transition-all duration-300 shadow-[0_0_20px_rgba(232,164,74,0.2)]"
              >
                {t('Demander un audit', 'Request an audit')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
